import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { fetchParentStudents, getActiveStudentId } from '../lib/parentStudents'

const GRADE_LEVELS = ['Junior 2', 'Junior 3', 'Junior 4', 'Junior 5', 'Junior 6', 'Middle 1', 'Middle 2', 'Middle 3', 'Senior 1', 'Senior 2']

function formatDate(value) {
  if (!value) return '—'
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

async function getSignedUrl(path) {
  const { data, error } = await supabase.storage.from('videos').createSignedUrl(path, 60 * 60)
  if (error) return null
  return data?.signedUrl || null
}

function VideoPlayer({ url, title }) {
  if (!url) return <div className="empty" style={{ margin: 0 }}>Video unavailable</div>
  return (
    <video
      src={url}
      controls
      playsInline
      preload="metadata"
      style={{ width: '100%', maxHeight: 430, borderRadius: 12, background: '#000', display: 'block' }}
      aria-label={title}
    />
  )
}

export default function Videos({ role }) {
  if (role === 'teacher' || role === 'assistant') return <StaffVideos />
  if (role === 'parent') return <ParentVideos />
  return <StudentVideos />
}

function StaffVideos() {
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [showTracker, setShowTracker] = useState(null)
  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [selectedGrades, setSelectedGrades] = useState([])
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [signedUrls, setSignedUrls] = useState({})
  const [tracker, setTracker] = useState({ loading: false, seen: [], unseen: [] })
  const fileRef = useRef(null)

  useEffect(() => { fetchVideos() }, [])

  async function fetchVideos() {
    setLoading(true)
    const { data, error } = await supabase.from('videos').select('*').order('created_at', { ascending: false })
    if (!error) {
      setVideos(data || [])
      const urls = {}
      await Promise.all((data || []).map(async v => { urls[v.id] = await getSignedUrl(v.storage_path) }))
      setSignedUrls(urls)
    }
    setLoading(false)
  }

  function toggleGrade(grade) {
    setSelectedGrades(prev => prev.includes(grade) ? prev.filter(g => g !== grade) : [...prev, grade])
  }

  async function addVideo(e) {
    e.preventDefault()
    if (!file) return alert('Please choose a video file.')
    if (!title.trim()) return alert('Please enter a video title.')
    if (!selectedGrades.length) return alert('Please select at least one grade.')

    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    const path = `${user.id}/${crypto.randomUUID()}-${safeName}`

    const { error: uploadError } = await supabase.storage.from('videos').upload(path, file, { upsert: false, contentType: file.type || 'video/mp4' })
    if (uploadError) { alert(uploadError.message); setSaving(false); return }

    const { data: video, error: insertError } = await supabase.from('videos').insert({
      title: title.trim(), note: note.trim() || null, storage_path: path, file_name: file.name, grade_levels: selectedGrades, created_by: user.id
    }).select().single()

    if (insertError) {
      await supabase.storage.from('videos').remove([path])
      alert(insertError.message)
      setSaving(false)
      return
    }

    const url = await getSignedUrl(path)
    setVideos(prev => [video, ...prev])
    setSignedUrls(prev => ({ ...prev, [video.id]: url }))
    setTitle(''); setNote(''); setSelectedGrades([]); setFile(null)
    if (fileRef.current) fileRef.current.value = ''
    setShowAdd(false)
    setSaving(false)
  }

  async function openTracker(video) {
    setShowTracker(video)
    setTracker({ loading: true, seen: [], unseen: [] })

    const { data: students } = await supabase.from('students').select('id, name, student_code, grade').in('grade', video.grade_levels || []).order('name')
    const { data: views } = await supabase.from('video_views').select('student_id, watched_at').eq('video_id', video.id)
    const seenMap = new Map((views || []).map(v => [v.student_id, v]))
    const seen = (students || []).filter(s => seenMap.has(s.id)).map(s => ({ ...s, watched_at: seenMap.get(s.id).watched_at }))
    const unseen = (students || []).filter(s => !seenMap.has(s.id))
    setTracker({ loading: false, seen, unseen })
  }

  async function deleteVideo(video) {
    if (!window.confirm(`Delete "${video.title}"?`)) return
    const { error } = await supabase.from('videos').delete().eq('id', video.id)
    if (error) return alert(error.message)
    await supabase.storage.from('videos').remove([video.storage_path])
    setVideos(prev => prev.filter(v => v.id !== video.id))
  }

  if (loading) return <div className="empty">Loading...</div>

  return (
    <div>
      <style>{`
        .video-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
        .video-card { background:var(--c-surface); border:1px solid var(--c-border); border-radius:14px; overflow:hidden; box-shadow:var(--c-shadow); }
        .video-card-body { padding:14px; }
        .video-thumb { background:#000; aspect-ratio:16/9; overflow:hidden; }
        .video-thumb video { width:100%; height:100%; object-fit:cover; }
        .grade-pills { display:flex; flex-wrap:wrap; gap:7px; }
        .grade-pill { border:1px solid var(--c-border); background:var(--c-surface); color:var(--c-text2); border-radius:999px; padding:7px 11px; font-size:12px; cursor:pointer; }
        .grade-pill.active { background:var(--c-accent-l); color:var(--c-accent); border-color:var(--c-accent); }
        .tracker-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .tracker-col { background:var(--c-surface2); border:1px solid var(--c-border); border-radius:12px; padding:12px; }
        .student-line { padding:9px 0; border-bottom:1px solid var(--c-border); }
        .student-line:last-child { border-bottom:0; }
        @media (max-width:767px) { .video-grid { grid-template-columns:1fr; } .tracker-grid { grid-template-columns:1fr; } }
      `}</style>

      <div className="page-header">
        <div>
          <h2 className="page-title">Videos</h2>
          <div style={{ fontSize:12, color:'var(--c-text3)', marginTop:4 }}>Share lesson videos with one or more grades.</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>＋ Add Video</button>
      </div>

      {!videos.length ? <div className="empty">No videos yet</div> : (
        <div className="video-grid">
          {videos.map(v => (
            <div className="video-card" key={v.id}>
              <div className="video-thumb">
                {signedUrls[v.id] ? <video src={signedUrls[v.id]} controls playsInline preload="metadata" /> : <div style={{ height:'100%', display:'grid', placeItems:'center', color:'#fff' }}>Loading video…</div>}
              </div>
              <div className="video-card-body">
                <div style={{ fontWeight:650, fontSize:16, overflowWrap:'anywhere' }}>{v.title}</div>
                {v.note && <div style={{ fontSize:13, color:'var(--c-text2)', marginTop:7, lineHeight:1.5, overflowWrap:'anywhere' }}>{v.note}</div>}
                <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:10 }}>
                  {(v.grade_levels || []).map(g => <span key={g} className="badge badge-success">{g}</span>)}
                </div>
                <div style={{ display:'flex', gap:8, marginTop:12 }}>
                  <button className="btn btn-secondary" style={{ flex:1 }} onClick={() => openTracker(v)}>➜ Track Students</button>
                  <button className="btn btn-danger-soft btn-sm" onClick={() => deleteVideo(v)}>Delete</button>
                </div>
                <div style={{ fontSize:11, color:'var(--c-text3)', marginTop:9 }}>{formatDate(v.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <form className="modal" onSubmit={addVideo}>
            <div className="modal-title">Add Video</div>
            <div className="field"><label>Video *</label><input ref={fileRef} type="file" accept="video/*" onChange={e => setFile(e.target.files?.[0] || null)} /></div>
            <div className="field"><label>Title *</label><input value={title} onChange={e => setTitle(e.target.value)} placeholder="Video title" required /></div>
            <div className="field">
              <label>Grades * (you can choose more than one)</label>
              <div className="grade-pills">{GRADE_LEVELS.map(g => <button type="button" key={g} className={`grade-pill ${selectedGrades.includes(g) ? 'active' : ''}`} onClick={() => toggleGrade(g)}>{selectedGrades.includes(g) ? '✓ ' : ''}{g}</button>)}</div>
            </div>
            <div className="field"><label>Note (optional)</label><textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Optional note for students and parents" /></div>
            <div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Uploading…' : 'Upload Video'}</button></div>
          </form>
        </div>
      )}

      {showTracker && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowTracker(null)}>
          <div className="modal" style={{ maxWidth:720 }}>
            <div className="modal-title">{showTracker.title} — Student Tracking</div>
            {tracker.loading ? <div className="empty">Loading students…</div> : (
              <div className="tracker-grid">
                <div className="tracker-col"><div style={{ fontWeight:650, marginBottom:6 }}>✓ Watched ({tracker.seen.length})</div>{tracker.seen.length ? tracker.seen.map(s => <div className="student-line" key={s.id}><div style={{ fontWeight:600 }}>{s.name}</div><div style={{ fontSize:11, color:'var(--c-text3)' }}>{s.student_code} · {formatDate(s.watched_at)}</div></div>) : <div style={{ color:'var(--c-text3)', fontSize:13 }}>No students yet.</div>}</div>
                <div className="tracker-col"><div style={{ fontWeight:650, marginBottom:6 }}>○ Not watched ({tracker.unseen.length})</div>{tracker.unseen.length ? tracker.unseen.map(s => <div className="student-line" key={s.id}><div style={{ fontWeight:600 }}>{s.name}</div><div style={{ fontSize:11, color:'var(--c-text3)' }}>{s.student_code}</div></div>) : <div style={{ color:'var(--c-text3)', fontSize:13 }}>Everyone has watched it.</div>}</div>
              </div>
            )}
            <div className="modal-actions"><button className="btn btn-primary" onClick={() => setShowTracker(null)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  )
}

function StudentVideos() {
  const [student, setStudent] = useState(null)
  const [videos, setVideos] = useState([])
  const [viewed, setViewed] = useState(new Set())
  const [urls, setUrls] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return setLoading(false)
    const { data: acc } = await supabase.from('student_accounts').select('student_id, students(id,name,grade)').eq('id', user.id).single()
    const s = acc?.students
    if (!s) return setLoading(false)
    setStudent(s)
    const { data } = await supabase.from('videos').select('*').contains('grade_levels', [s.grade]).order('created_at', { ascending:false })
    const list = data || []
    const { data: views } = await supabase.from('video_views').select('video_id').eq('student_id', s.id)
    setVideos(list)
    setViewed(new Set((views || []).map(v => v.video_id)))
    const map = {}
    await Promise.all(list.map(async v => { map[v.id] = await getSignedUrl(v.storage_path) }))
    setUrls(map)
    setLoading(false)
  }

  async function markWatched(videoId) {
    if (!student) return
    const { error } = await supabase.from('video_views').upsert({ video_id: videoId, student_id: student.id, watched_at: new Date().toISOString() }, { onConflict:'video_id,student_id' })
    if (error) return alert(error.message)
    setViewed(prev => new Set([...prev, videoId]))
  }

  if (loading) return <div className="empty">Loading...</div>
  return (
    <div>
      <h2 className="page-title" style={{ marginBottom:6 }}>Videos</h2>
      {student && <div style={{ fontSize:13, color:'var(--c-text3)', marginBottom:18 }}>Videos for {student.name}</div>}
      {!videos.length ? <div className="empty">No videos yet</div> : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,360px),1fr))', gap:16 }}>{videos.map(v => <div className="card" key={v.id} style={{ padding:14 }}><VideoPlayer url={urls[v.id]} title={v.title} /><div style={{ marginTop:12, fontWeight:650, fontSize:16, overflowWrap:'anywhere' }}>{v.title}</div>{v.note && <div style={{ marginTop:7, color:'var(--c-text2)', fontSize:13, lineHeight:1.5, overflowWrap:'anywhere' }}>{v.note}</div>}<button className={`btn ${viewed.has(v.id) ? 'btn-secondary' : 'btn-primary'}`} style={{ width:'100%', marginTop:13 }} onClick={() => markWatched(v.id)}>{viewed.has(v.id) ? 'Watch Again' : 'When you finish the video, press this button'}</button></div>)}</div>}
    </div>
  )
}

function ParentVideos() {
  const [student, setStudent] = useState(null)
  const [videos, setVideos] = useState([])
  const [urls, setUrls] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return setLoading(false)
      const { students } = await fetchParentStudents(user.id)
      const activeId = getActiveStudentId(students)
      const s = students.find(x => x.id === activeId)
      if (!s) return setLoading(false)
      setStudent(s)
      const { data } = await supabase.from('videos').select('*').contains('grade_levels', [s.grade]).order('created_at', { ascending:false })
      const list = data || []
      setVideos(list)
      const map = {}
      await Promise.all(list.map(async v => { map[v.id] = await getSignedUrl(v.storage_path) }))
      setUrls(map)
      setLoading(false)
    }
    fetchData()
    window.addEventListener('parent-student-changed', fetchData)
    return () => window.removeEventListener('parent-student-changed', fetchData)
  }, [])

  if (loading) return <div className="empty">Loading...</div>
  return (
    <div>
      <h2 className="page-title" style={{ marginBottom:6 }}>Videos</h2>
      {student && <div style={{ fontSize:13, color:'var(--c-text3)', marginBottom:18 }}>Showing videos for <strong style={{ color:'var(--c-text2)' }}>{student.name}</strong></div>}
      {!videos.length ? <div className="empty">No videos yet</div> : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,360px),1fr))', gap:16 }}>{videos.map(v => <div className="card" key={v.id} style={{ padding:14 }}><VideoPlayer url={urls[v.id]} title={v.title} /><div style={{ marginTop:12, fontWeight:650, fontSize:16, overflowWrap:'anywhere' }}>{v.title}</div>{v.note && <div style={{ marginTop:7, color:'var(--c-text2)', fontSize:13, lineHeight:1.5, overflowWrap:'anywhere' }}>{v.note}</div>}</div>)}</div>}
    </div>
  )
}
